import React, { useEffect, useMemo, useRef, useState } from "react";
import { Card, Grid, Icon, IconButton } from "@mui/material";
import CenterPopup from "components/CommonComponent/RightPanelDrawer";
import TimeSheetStepper from "./TimeSheetStepper";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { useLocation, useNavigate } from "react-router-dom";
import DataTable from "examples/Tables/DataTable";
import { showAlert } from "components/CommonComponent/ShowAlert";
import { renderHeader } from "components/CommonComponent/CommonFunction";
import { useAuth } from "pages/Auth/AuthContext";
import { useDispatch, useSelector } from "react-redux";
import {
  getTimeSheetRecordDetailById,
  addTimeSheetRecord,
} from "actions/timesheet";
import { useNotify } from "components/CommonComponent/NotificationProvider";
import { productList } from "constants/product";
import { getShiftList } from "actions/shift";
import { shiftList } from "constants/shift";
import { getDutyTypeList } from "actions/dutyType";
import { dutyTypeList } from "constants/dutyType";
import { getProjectList } from "actions/project";
import { getUnitListing } from "actions/unit";
import { getSubActivityList } from "actions/subActivity";
import { getMicroActivityList } from "actions/microActivity";
import { getRemarkList } from "actions/remark";

const STORAGE_KEY = "timesheet_draft";

function AddTimeSheet() {
  const originalRowsRef = useRef([]);
  const { notify } = useNotify();
  const user = JSON.parse(localStorage.getItem("pt") || "{}");
  const dispatch = useDispatch();
  //reamrk
  const remarkListData = useSelector((state) => state.remark.remarkList);

  const dutyTypeListData = useSelector((state) => state.dutyType.dutyTypeList);
  //shift listing
  const shiftListData = useSelector((state) => state.shift.shiftList);
  const remarkslist = remarkListData?.data?.remarks || [];
  const shifts = shiftListData?.data?.shift || [];
  const dutyTypes = dutyTypeListData?.data?.DutyType || [];

  const navigate = useNavigate();
  const { logoutUser } = useAuth();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const shiftId = queryParams.get("shiftId");
  const dutyTypeId = queryParams.get("dutyTypeId");

  const getDate = queryParams.get("date");

  const parsedDate = getDate ? dayjs(getDate, "DD-MM-YYYY") : dayjs();

  const currentShift = useMemo(() => {
    return shifts.find((s) => s.ID === Number(shiftId));
  }, [shifts, shiftId]);

  const mode = queryParams.get("mode");
  const timesheetId = queryParams.get("id");

  const [open, setOpen] = useState(false);
  const [timesheetDetail, setTimesheetDetail] = useState(null);
  const [tableRows, setTableRows] = useState([]); // draft (inside popup)
  const [finalRows, setFinalRows] = useState([]); // final (outside)
  const [technicianInfo, setTechnicianInfo] = useState({
    name: "",
    employeeID: "",
    department: { name: "", code: "" },
    shift: "",
    dutyType: "",
  });
  const [isViewMode, setIsViewMode] = useState(false);
  const [editingRowId, setEditingRowId] = useState(null);
  const [stepperRowData, setStepperRowData] = useState(null);
  const [draftModalShown, setDraftModalShown] = useState(false);
  const [showAlternative, setShowAlternative] = useState({
    loader: false,
    error: "",
  });

  useEffect(() => {
    getShiftList(dispatch);
    getDutyTypeList(dispatch);
    return () => {
      dispatch({ type: shiftList, payload: {} });
      dispatch({ type: dutyTypeList, payload: {} });
      dispatch({ type: productList, payload: {} });
    };
  }, [dispatch]);

  useEffect(() => {
    if (mode === "view") {
      setIsViewMode(true);
      setOpen(false);

      if (timesheetId) {
        getTimeSheetDetails(timesheetId);
      }
    } else if (mode === "add") {
      setIsViewMode(false);
      setOpen(true);

      const lhbNo = queryParams.get("lhbNo");

      const shift = shifts.find((s) => s.ID === Number(shiftId));
      const duty = dutyTypes.find((d) => d.ID === Number(dutyTypeId));

      setTechnicianInfo({
        name: user?.name || "",
        employeeID: user?.employeeId || "",
        department: user?.department || {},
        shift: shift?.name || "-",
        dutyType: duty?.name || "-",
      });

      if (lhbNo) {
        getTimeSheetDetails(lhbNo);
      }
    }
  }, [mode, timesheetId]);

  useEffect(() => {
    if (isViewMode) return;
    const draft = localStorage.getItem(STORAGE_KEY);
    if (draft && !draftModalShown && user?.employeeId === draft?.employeeId) {
      try {
        const parsed = JSON.parse(draft);
        if (parsed?.entries?.length) {
          setDraftModalShown(true);
          setTableRows(parsed.entries);

          setTimeout(() => {
            showAlert({
              title: "Draft TimeSheet Found",
              message:
                "We found a previously saved draft of your timesheet. Would you like to continue filling it or start fresh?",
              type: "info",
              showCancel: true,
              confirmText: "Continue Draft",
              cancelText: "Discard",
              onConfirm: () => {
                setOpen(true);
              },
              onCancel: () => {
                localStorage.removeItem(STORAGE_KEY);
                setTableRows([]);
                setFinalRows([]);
              },
            });
          }, 300);
        }
      } catch (error) {
        console.error("Error reading timesheet draft from localStorage", error);
      }
    }
  }, [isViewMode]);

  const isOvertimeDuty = useMemo(() => {
    const duty =
      dutyTypes.find((d) => d.ID === Number(dutyTypeId)) ||
      timesheetDetail?.dutyType;

    const dutyName = duty?.name?.toLowerCase();

    return dutyName === "overtime" || dutyName === "outdoor";
  }, [dutyTypes, dutyTypeId, timesheetDetail]);

  const handleEdit = (row) => {
    const product = row.productObj;
    const project = row.projectObj;
    const unit = row.unitObj;
    const station = row.stationObj;
    const subActivity = row.subActivityObj;
    const microActivity = row.microActivityObj;
    const remarkObj = row.remarkObj;

    if (product?.ID) {
      getProjectList(
        dispatch,
        null,
        JSON.stringify([
          {
            field: "ProductID",
            condition: "eq",
            filterValues: [String(product.ID)],
          },
        ]),
      );
    }

    if (project?.ID) {
      getUnitListing(
        dispatch,
        null,
        JSON.stringify([
          {
            field: "ProjectID",
            condition: "eq",
            filterValues: [String(project.ID)],
          },
        ]),
      );
    }

    if (station?.ID) {
      getSubActivityList(
        dispatch,
        null,
        JSON.stringify([
          {
            field: "StationID",
            condition: "eq",
            filterValues: [String(station.ID)],
          },
        ]),
      );
    }

    if (subActivity?.ID) {
      getMicroActivityList(
        dispatch,
        null,
        JSON.stringify([
          {
            field: "SubActivityID",
            condition: "eq",
            filterValues: [String(subActivity.ID)],
          },
        ]),
      );
    }

    getRemarkList(dispatch);

    const rowData = {
      productType: product,
      projectNumber: project,
      unitNo: unit,
      subUnit: row.subUnit,
      stationName: station,
      subActivity: subActivity,
      microActivity: microActivity,
      remark: remarkObj?.ID && remarkObj?.remark ? remarkObj : row.remark || "",
      activityType: row.activityType?.toUpperCase() || "",
      hours: row.hours?.split(":")[0] || null,
      minutes: row.hours?.split(":")[1] || null,
    };

    setEditingRowId(row.id);
    setStepperRowData(rowData);

    setTimeout(() => {
      setOpen(true);
    }, 100);
  };

  const handleDelete = (row) => {
    showAlert({
      title: "Are you sure you want to delete this entry?",
      message: "Entry will be removed from the list.",
      type: "warning",
      showCancel: true,
      confirmText: "Yes, Delete",
      cancelText: "Cancel",

      onConfirm: () => {
        const updatedRows = tableRows.filter((r) => r.id !== row.id);

        setTableRows(updatedRows);
        setFinalRows(updatedRows);

        notify("Record removed successfully", "success");
      },
    });
  };

  const getTimeSheetDetails = async (id) => {
    try {
      setShowAlternative({ loader: true, error: "" });

      const res = await getTimeSheetRecordDetailById(id);

      if (res?.code === 200) {
        const data = res.data;

        setTimesheetDetail(data);

        setTechnicianInfo({
          name: data?.technician?.name || "",
          employeeID: data?.technician?.employeeID || "",
          department: {
            name: data?.technician?.department?.name || "",
            code: data?.technician?.department?.code || "",
          },
          shift: data?.shift?.name || "",
          dutyType: data?.dutyType?.name || "",
        });

        const rows =
          data?.lhbRecords?.map((item) => {
            const hh = Math.floor(item.durationMinutes / 60);
            const mm = item.durationMinutes % 60;

            return {
              id: item.ID,
              productType: item.product?.type,
              productObj: item.product || null,
              projectName: item.projectID?.projectNo,
              projectObj: item.projectID || null,
              unitNo: item.Unit?.unitNo,
              unitObj: item.Unit || null,
              subUnit: item.subUnit || "",
              stationName: item.Station?.name,
              stationObj: item.Station || null,
              subActivity: item.SubActivity?.name,
              subActivityObj: item.SubActivity || null,
              microActivity: item.MicroActivity?.name,
              microActivityObj: item.MicroActivity || null,

              hours: `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`,

              remark: item.customRemark,
              remarkObj: item.Remark || null,
              activityType: item.activityType,
            };
          }) || [];

        setTableRows(rows);
        setFinalRows(rows);

        originalRowsRef.current = {
          rows: JSON.parse(JSON.stringify(rows)),
          lhbNo: data?.lhbNo,
          timesheetId: data?.ID,
        };
      } else {
        setShowAlternative({
          loader: false,
          error: res?.message || "Failed to load data",
        });
        return;
      }

      setShowAlternative({ loader: false, error: "" });
    } catch (error) {
      setShowAlternative({
        loader: false,
        error: "Something went wrong",
      });
    }
  };

  const tableDataWithSrNo = useMemo(() => {
    return (tableRows || []).map((item, i) => ({
      ...item,
      Srno: i + 1,
    }));
  }, [tableRows]);

  const columns = useMemo(() => {
    const baseColumns = [
      {
        Header: () => renderHeader("Sr No"),
        accessor: "Srno",
        Cell: (row) => (
          <MDTypography variant="h6" color="text" textAlign="center">
            {row.Srno}
          </MDTypography>
        ),
      },
      {
        Header: () => renderHeader("Project Detail"),
        accessor: "projectDetails",
        align: "left",
        width: "160px",
        Cell: (row) => (
          <MDBox>
            <MDTypography variant="h6" color="text">
              {row.productType || "-"}
            </MDTypography>

            <MDTypography variant="h6" color="text">
              {row.projectName?.projectNo || row.projectName || "-"}
            </MDTypography>

            <MDTypography variant="h6" color="text">
              {row.unitNo?.unitNo || row.unitNo || "-"}
            </MDTypography>

            <MDTypography variant="h6" color="text" display="block">
              {row.subUnit || "-"}
            </MDTypography>

            <MDTypography variant="h6" color="text">
              {row.stationName?.name || row.stationName || "-"}
            </MDTypography>
          </MDBox>
        ),
      },
      {
        Header: () => renderHeader("Activity"),
        accessor: "activity",
        align: "left",
        width: "150px",
        Cell: (row) => (
          <MDBox sx={{ maxWidth: "150px", wordBreak: "break-word" }}>
            <MDTypography
              variant="h6"
              color="text"
              sx={{ whiteSpace: "normal", lineHeight: 1.3 }}
            >
              {row.subActivity?.name || row.subActivity || ""}
            </MDTypography>

            <MDTypography
              variant="h6"
              color="text"
              pt={2}
              sx={{ whiteSpace: "normal", lineHeight: 1.3 }}
            >
              {row.microActivity?.name || row.microActivity || ""}
            </MDTypography>
          </MDBox>
        ),
      },
      {
        Header: () => renderHeader("Hours"),
        accessor: "hours",
        align: "left",
        width: "120px",
        Cell: (row) => (
          <MDTypography variant="h6" color="text" textAlign="center">
            {row.hours}
          </MDTypography>
        ),
      },
      {
        Header: () => renderHeader("Remark"),
        accessor: "remark",
        align: "left",
        width: "160px",
        Cell: (row) => (
          <MDBox sx={{ maxWidth: "160px", wordBreak: "break-word" }}>
            <MDTypography
              variant="h6"
              color="text"
              sx={{
                whiteSpace: "normal",
                wordBreak: "break-word",
                lineHeight: 1.3,
              }}
            >
              {row.activityType || "Fresh"}
            </MDTypography>

            <MDTypography
              variant="h6"
              color="text"
              sx={{
                whiteSpace: "normal",
                wordBreak: "break-word",
                lineHeight: 1.3,
              }}
            >
              {row.remark?.remark || row.remark || ""}
            </MDTypography>
          </MDBox>
        ),
      },
    ];

    if (open && !isViewMode) {
      baseColumns.push({
        Header: () => renderHeader("Action"),
        accessor: "actions",
        align: "right",
        width: "100px",
        disableSortBy: true,
        Cell: (row) => (
          <MDBox display="flex" alignItems="center" gap={1}>
            <Icon
              sx={({ palette }) => ({
                cursor: "pointer",
                color: palette.secondary.main,
                width: "2em",
                height: "2em",
              })}
              onClick={() => handleEdit(row)}
            >
              <i className="fas fa-edit fa-lg" />
            </Icon>

            <Icon
              color="error"
              sx={{
                cursor: "pointer",
                opacity: 0.5,
                "&:hover": { opacity: 1 },
                width: "2em",
                height: "2em",
              }}
              onClick={() => handleDelete(row)}
            >
              <i className="fas fa-trash fa-lg" />
            </Icon>
          </MDBox>
        ),
      });
    }

    return baseColumns;
  }, [open, isViewMode, tableRows]);

  const formatTime = (hours, minutes) => {
    const hh = String(hours).padStart(2, "0");
    const mm = String(minutes || 0).padStart(2, "0");
    return minutes !== null && minutes !== undefined ? `${hh}:${mm}` : hh;
  };

  const getTotalHours = () => {
    let totalHours = 0;
    let totalMinutes = 0;

    tableRows.forEach((row) => {
      if (!row.hours) return;
      const parts = row.hours.split(":");
      const hh = parseInt(parts[0], 10);
      const mm = parts[1] ? parseInt(parts[1], 10) : 0;
      totalHours += hh;
      totalMinutes += mm;
    });
    // Convert minutes to hours
    totalHours += Math.floor(totalMinutes / 60);
    totalMinutes = totalMinutes % 60;
    const hhStr = String(totalHours).padStart(2, "0");
    const mmStr = String(totalMinutes).padStart(2, "0");
    return `${hhStr}:${mmStr}`;
  };

  const getRemainingHours = () => {
    const requiredMinutes = getRequiredMinutes();

    if (requiredMinutes === 0) {
      return "Unlimited";
    }

    const total = getTotalHours();
    const [hh, mm] = total.split(":").map(Number);

    const totalMinutesUsed = hh * 60 + mm;
    const remainingMinutes = requiredMinutes - totalMinutesUsed;

    if (remainingMinutes <= 0) return "00:00";

    const rH = Math.floor(remainingMinutes / 60);
    const rM = remainingMinutes % 60;

    return `${String(rH).padStart(2, "0")}:${String(rM).padStart(2, "0")}`;
  };

  const handleClose = () => {
    // Always show confirmation dialog when closing
    if (open) {
      let message = "Do you want to discard your entries and close the form?";
      if (tableRows.length > 0 && !finalRows.length) {
        message =
          "You have unsaved changes in your draft. Do you want to discard them and start over?";
      }

      showAlert({
        title: "Discard Entries?",
        message: message,
        type: "warning",
        showCancel: true,
        confirmText: "Yes, Discard",
        cancelText: "Continue Editing",
        onConfirm: () => {
          localStorage.removeItem(STORAGE_KEY);
          setTableRows([]);
          setFinalRows([]);
          setOpen(false);
          setEditingRowId(null);
          // setSelectedLhb("");
        },
        onCancel: () => {
          // Keep editing - modal stays open
        },
      });
    }
  };

  const handleAddTimesheet = async (data, isFinal = false) => {
    // Skip validation for overtime duties
    if (!isOvertimeDuty) {
      const enteredMinutes =
        Number(data.hours || 0) * 60 + Number(data.minutes || 0);

      const otherRowsMinutes = tableRows
        .filter((row) => row.id !== editingRowId)
        .reduce((acc, row) => {
          const [hh, mm] = String(row.hours || "00:00")
            .split(":")
            .map(Number);

          return acc + hh * 60 + (mm || 0);
        }, 0);

      const requiredMinutes = getRequiredMinutes();
      const totalMinutes = otherRowsMinutes + enteredMinutes;

      if (totalMinutes > requiredMinutes) {
        const exceededMinutes = totalMinutes - requiredMinutes;

        notify(
          `Time limit exceeded by ${Math.floor(
            exceededMinutes / 60,
          )} hr ${exceededMinutes % 60} min. Maximum allowed is ${Math.floor(
            requiredMinutes / 60,
          )} hr ${requiredMinutes % 60} min.`,
          "warning",
        );

        return;
      }
    }

    if (editingRowId) {
      const updatedRow = {
        ...tableRows.find((r) => r.id === editingRowId),

        productType: data.productType?.type,
        projectName: data.projectNumber?.projectNo,
        unitNo: data.unitNo?.unitNo,
        subUnit: data.subUnit,
        stationName: data.stationName?.name,

        subActivity: data.subActivity?.name || data.subActivity,
        microActivity: data.microActivity?.name || data.microActivity,

        remark:
          typeof data.remark === "object"
            ? data.remark.remark
            : data.remark || "",

        remarkObj:
          typeof data.remark === "object"
            ? data.remark
            : remarkslist.find((r) => r.remark === data.remark) || null,

        productObj: data.productType,
        projectObj: data.projectNumber,
        unitObj: data.unitNo,
        stationObj: data.stationName,
        subActivityObj: data.subActivity,
        microActivityObj: data.microActivity,

        hours: formatTime(data.hours, data.minutes),
        activityType: data.activityType,
      };

      setTableRows((prev) =>
        prev.map((row) => (row.id === editingRowId ? updatedRow : row)),
      );

      notify("Row updated successfully", "success");

      setEditingRowId(null);
      setStepperRowData(null);
      return;
    }
    setTableRows((prev) => {
      const newRow = {
        id: prev.length + 1,

        productType: data.productType?.type,
        projectName: data.projectNumber?.projectNo,
        unitNo: data.unitNo?.unitNo,
        subUnit: data.subUnit ?? "",
        stationName: data.stationName?.name,

        subActivity: data.subActivity?.name || data.subActivity,
        microActivity: data.microActivity?.name || data.microActivity,

        remark:
          typeof data.remark === "object"
            ? data.remark.remark
            : data.remark || "",

        remarkObj:
          typeof data.remark === "object"
            ? data.remark
            : remarkslist.find((r) => r.remark === data.remark) || null,

        productObj: data.productType,
        projectObj: data.projectNumber,
        unitObj: data.unitNo,
        stationObj: data.stationName,
        subActivityObj: data.subActivity,
        microActivityObj: data.microActivity,

        hours: formatTime(data.hours, data.minutes),
        activityType: data.activityType,
      };

      return [...prev, newRow];
    });
    setStepperRowData(null);
    if (isFinal) {
      setOpen(false);
    }
  };

  const buildLhbRecordsPayload = () => {
    return tableRows.map((row) => {
      const [hh, mm] = row.hours.split(":").map(Number);
      const totalMinutes = hh * 60 + (mm || 0);
      console.log(totalMinutes);

      return {
        productID: row.productObj?.ID || null,
        projectID: row.projectObj?.ID || null,
        unitID: row.unitObj?.ID || null,
        subUnit: row.subUnit || null,
        stationID: row.stationObj?.ID || null,

        subActivityID: row.subActivityObj?.ID || null,
        microActivityID: row.microActivityObj?.ID || null,

        durationMinutes: totalMinutes,
        activityType: row.activityType,

        remarkID: row.remarkObj?.ID || null,
        customRemark: row.remark || "",

        status: "PENDING",
      };
    });
  };

  const shiftHoursToMinutes = (value) => {
    if (!value) return 0;
    const [hh, mm] = String(value).split(".").map(Number);
    return (hh || 0) * 60 + (mm || 0);
  };

  const getRequiredMinutes = () => {
    const duty = dutyTypes.find((d) => d.ID === Number(dutyTypeId));
    const dutyName = (duty?.name || "").toLowerCase().trim();

    // Unlimited
    if (dutyName === "overtime" || dutyName === "outdoor") {
      return 0;
    }

    // Regular to Shift Master
    if (dutyName === "regular") {
      return shiftHoursToMinutes(
        currentShift?.formattedHours || currentShift?.totalHours,
      );
    }

    // Half Day & future duty types
    const dutyHours = duty?.totalHours;

    // 0/null unlimited
    if (!dutyHours) {
      return 0;
    }

    return shiftHoursToMinutes(dutyHours);
  };

  const isValidTotalHours = () => {
    const total = getTotalHours();
    const [hh, mm] = total.split(":").map(Number);
    const totalMinutes = hh * 60 + mm;
    const requiredMinutes = getRequiredMinutes();

    // Unlimited duty types
    if (requiredMinutes === 0) {
      return totalMinutes > 0;
    }
    return totalMinutes === requiredMinutes;
  };

  const getRequiredHoursText = () => {
    const mins = getRequiredMinutes();
    const hh = String(Math.floor(mins / 60)).padStart(2, "0");
    const mm = String(mins % 60).padStart(2, "0");
    return `${hh}:${mm}`;
  };

  const buildTimesheetPayload = () => {
    const changed = !isSameAsExistingLhb();

    const originalLhbNo = originalRowsRef.current?.lhbNo;

    return {
      shiftId: Number(shiftId) || null,
      dutyTypeId: Number(dutyTypeId) || null,
      shiftStartDate: parsedDate.format("YYYY-MM-DD"),
      totalDurationMinutes: tableRows.reduce((acc, row) => {
        const [hh, mm] = row.hours.split(":").map(Number);
        return acc + (hh * 60 + (mm || 0));
      }, 0),
      status: "PENDING",
      ...(originalLhbNo && !changed ? { lhbNo: originalLhbNo } : {}),
    };
  };
  const handleFinalSubmit = async () => {
    const payload = {
      timesheet: buildTimesheetPayload(),
      lhbRecords: buildLhbRecordsPayload(),
    };
    try {
      const res = await addTimeSheetRecord(payload);

      if (res?.code === 200 || res?.status === "SUCCESS") {
        const newLhbNo = res?.data;

        setFinalRows(tableRows);
        setOpen(false);
        localStorage.removeItem(STORAGE_KEY);

        setTimesheetDetail((prev) => ({
          ...prev,
          ID: res?.data || prev?.ID,
        }));

        setTechnicianInfo({
          name: user?.name || "",
          employeeID: user?.employeeId || "",
          department: user?.department || {},
          shift: technicianInfo.shift,
          dutyType: technicianInfo.dutyType,
        });

        originalRowsRef.current = {
          rows: JSON.parse(JSON.stringify(tableRows)),
          timesheetId: timesheetDetail?.ID,
        };
        if (newLhbNo) {
          showAlert({
            title: "Timesheet Submitted Successfully!",
            message: (
              <div style={{ textAlign: "center" }}>
                <div
                  style={{
                    fontWeight: "bold",
                    marginBottom: "10px",
                    fontSize: "18px",
                  }}
                >
                  LHB No: {newLhbNo}
                </div>

                <div style={{ fontSize: "1rem", color: "#555" }}>
                  This LHB number has been generated successfully. Please share
                  it with your colleague for further reference.
                </div>
              </div>
            ),
            type: "success",
            confirmText: "OK",
          });
        } else {
          notify("Timesheet updated successfully", "success");
        }
      } else {
        const message = Array.isArray(res?.message)
          ? res.message.map((err) => err.Msg || JSON.stringify(err)).join(", ")
          : res?.message || "Failed to save timesheet";

        notify(message, "error");
      }
    } catch (err) {
      notify(err.message || err.message || "Server error", "error");
    }
  };

  const normalizeLhb = (rows) =>
    rows
      .map((r) => ({
        productID: r.productObj?.ID || null,
        projectID: r.projectObj?.ID || null,
        unitID: r.unitObj?.ID || null,
        stationID: r.stationObj?.ID || null,
        subActivityID: r.subActivityObj?.ID || null,
        microActivityID: r.microActivityObj?.ID || null,
        activityType: (r.activityType || "").toUpperCase(),
        remarkID: r.remarkObj?.ID || null,
        hours: r.hours || "",
      }))
      .sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));

  const isSameAsExistingLhb = () => {
    const original = originalRowsRef.current?.rows || [];
    const current = tableRows || [];

    if (!original.length && !current.length) return true;

    const orig = JSON.stringify(normalizeLhb(original));
    const curr = JSON.stringify(normalizeLhb(current));

    return orig === curr;
  };
  return (
    <DashboardLayout>
      <MDBox>
        <Grid container>
          <Grid item xs={12}>
            <Card sx={{ p: 3 }}>
              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <MDBox display="flex" alignItems="center" gap={1}>
                  <IconButton
                    size="small"
                    onClick={() => navigate(-1)}
                    sx={{ p: 0.5 }}
                  >
                    <ArrowBackIcon fontSize="small" />
                  </IconButton>

                  <MDTypography variant="h5" fontWeight="bold">
                    Timesheet
                  </MDTypography>
                </MDBox>

                <MDBox display="flex" alignItems="center">
                  {/* <Icon sx={{ mr: 1 }}>account_circle</Icon>

                  <MDTypography variant="body1" fontWeight="medium" sx={{ mr: 1 }}>
                    Welcome, {employeeInfo.name || "Employee"}
                  </MDTypography> */}
                  <MDButton
                    variant="gradient"
                    color="light"
                    onClick={logoutUser}
                    startIcon={<Icon>logout</Icon>}
                    sx={{
                      textTransform: "none",
                      fontWeight: 500,
                      fontSize: "1rem",
                    }}
                  >
                    Logout
                  </MDButton>
                </MDBox>
              </MDBox>

              <MDBox
                sx={{
                  backgroundColor: "#f5f6f8",
                  borderRadius: 2,
                  p: 2,
                  mb: 3,
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <MDTypography variant="body2">
                      LHB No:
                      <strong>{timesheetDetail?.ID || "-"}</strong>
                    </MDTypography>
                  </Grid>

                  <Grid item xs={3}>
                    <MDTypography variant="body2">
                      Date:
                      <strong>
                        {getDate
                          ? dayjs(getDate, "DD-MM-YYYY").format("DD-MM-YYYY")
                          : timesheetDetail?.shiftStartDate
                            ? dayjs(timesheetDetail.shiftStartDate).format(
                                "DD-MM-YYYY",
                              )
                            : dayjs().format("DD-MM-YYYY")}
                      </strong>
                    </MDTypography>
                  </Grid>

                  <Grid item xs={3}>
                    <MDTypography variant="body2">
                      Emp No :
                      <strong>{technicianInfo.employeeID || "-"}</strong>
                    </MDTypography>
                  </Grid>

                  <Grid item xs={3}>
                    <MDTypography variant="body2">
                      Department :
                      <strong>
                        {technicianInfo?.department?.name || "N/A"}
                      </strong>
                    </MDTypography>
                  </Grid>

                  <Grid item xs={3}>
                    <MDTypography variant="body2">
                      Dept Code :
                      <strong>
                        {technicianInfo?.department?.code || "N/A"}
                      </strong>
                    </MDTypography>
                  </Grid>

                  <Grid item xs={3}>
                    <MDTypography variant="body2">
                      Duty Type :
                      <strong>
                        {mode === "view"
                          ? timesheetDetail?.dutyType?.name || "-"
                          : technicianInfo.dutyType || "-"}
                      </strong>
                    </MDTypography>
                  </Grid>

                  <Grid item xs={3}>
                    <MDTypography variant="body2">
                      Shift :
                      <strong>
                        {mode === "view"
                          ? timesheetDetail?.shift?.name || "-"
                          : technicianInfo.shift || "-"}
                      </strong>
                    </MDTypography>
                  </Grid>
                </Grid>
              </MDBox>

              {open && (
                <CenterPopup isOpen={open} onClose={handleClose} width={1250}>
                  <TimeSheetStepper
                    key={editingRowId ? `edit-${editingRowId}` : "new"}
                    onClose={handleClose}
                    onSubmit={handleAddTimesheet}
                    rowData={stepperRowData}
                    isEditMode={!!editingRowId}
                    remainingMinutes={
                      getRequiredMinutes() -
                      tableRows
                        .filter((row) => row.id !== editingRowId)
                        .reduce((acc, row) => {
                          const [hh, mm] = row.hours.split(":").map(Number);
                          return acc + (hh * 60 + (mm || 0));
                        }, 0)
                    }
                    isOvertimeDuty={isOvertimeDuty}
                  />

                  {tableRows.length > 0 && (
                    <MDBox mt={2}>
                      <MDBox
                        px={3}
                        py={1}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <MDTypography fontWeight="bold" variant="h4">
                          Total Records: {tableRows?.length || 0}
                        </MDTypography>

                        <MDBox textAlign="right">
                          <MDTypography fontWeight="bold" variant="h4">
                            Added Hours: {getTotalHours()}
                          </MDTypography>

                          {!isOvertimeDuty && (
                            <MDTypography
                              fontWeight="medium"
                              variant="h4"
                              color={
                                getRemainingHours() === "00:00"
                                  ? "success"
                                  : "warning"
                              }
                            >
                              Remaining Hours: {getRemainingHours()}
                            </MDTypography>
                          )}
                        </MDBox>
                      </MDBox>
                      <MDBox sx={{ overflowX: "auto" }}>
                        <DataTable
                          columns={columns}
                          data={tableDataWithSrNo || []}
                          isSorted={true}
                          entriesPerPage={false}
                          showTotalEntries={false}
                          noEndBorder
                        />
                      </MDBox>

                      <MDBox
                        px={3}
                        py={2}
                        display="flex"
                        flexDirection="column"
                        alignItems="flex-end"
                        gap={1}
                        mt={-5}
                      >
                        <MDButton
                          variant="contained"
                          color="deepBlue"
                          onClick={handleFinalSubmit}
                          disabled={!isOvertimeDuty && !isValidTotalHours()}
                          sx={{
                            px: 4,
                            py: 1.2,
                            fontSize: "1.1rem",
                            fontWeight: 600,
                          }}
                        >
                          Submit
                        </MDButton>

                        {tableRows.length > 0 &&
                          !isOvertimeDuty &&
                          !isValidTotalHours() && (
                            <MDTypography color="error" fontSize="1rem">
                              {isOvertimeDuty
                                ? "Please add at least some hours to submit"
                                : `Total hours must be exactly ${getRequiredHoursText()} to submit`}
                            </MDTypography>
                          )}
                      </MDBox>
                    </MDBox>
                  )}
                </CenterPopup>
              )}

              {finalRows.length > 0 && !open && (
                <MDBox mt={3}>
                  <MDBox
                    px={3}
                    py={1}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <MDTypography variant="body1">
                      Total Records: {finalRows?.length || 0}
                    </MDTypography>
                  </MDBox>

                  <MDBox sx={{ overflowX: "auto" }}>
                    <DataTable
                      columns={columns}
                      data={tableDataWithSrNo || []}
                      isSorted
                      entriesPerPage={false}
                      showTotalEntries={false}
                      noEndBorder
                    />
                  </MDBox>

                  <MDBox
                    px={3}
                    py={2}
                    display="flex"
                    justifyContent="flex-end"
                    alignItems="center"
                    gap={2}
                  >
                    <MDTypography fontWeight="bold" variant="h4">
                      Total Hours: {getTotalHours()}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default AddTimeSheet;
